import {
  Backdrop,
  Box,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useContractDetails } from '../../services/contractDetails';
import { BaseController } from '../BaseController/baseController';
import { CustomButton } from '../common/button/customButton';
import { CustomMessageToast } from '../common/messageToast/messageToast';
import { ContactForm } from '../contactForm/contactForm';
import { ErrorBox } from '../error/error';
import { GoToTopButton } from '../goToTopButton/goToTopButton';
import { ButtonContent } from './buttonContent/buttonContent';
import { ContractData } from './contractData/contractData';
import { CustomerContent } from './customerContent/customerContent';
import { Downloads } from './downloads/downloads';
import { InvoiceData } from './invoiceData/invoiceData';
import { ProductImage } from './productImage/productImage';
import { Usage } from './usage/usage';

export interface DetailContentProps {
  contractNumber: string;
  contractPosition: string;
  showMessage?: boolean;
}

export const DetailContent = (props: DetailContentProps) => {
  const { contractNumber, contractPosition, showMessage } = props;
  const { t } = useTranslation();

  const [showContactForm, setShowContactForm] = useState(false);
  const onClose = () => {
    setShowContactForm(false);
  };

  const { contractDetails, isLoading, serverError } = useContractDetails({
    contractNumber,
    contractPosition,
  });

  if (isLoading) {
    return (
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (!isLoading && serverError) {
    return (
      <ErrorBox errorHeader={t('MissingRights')} errorText={t(serverError)} />
    );
  }

  return (
    <Box
      sx={{
        flexDirection: 'column',
        width: '100%',
        backgroundColor: 'rgb(241,241,241)',
      }}
    >
      <ContactForm
        open={showContactForm}
        onClose={onClose}
        contractType={contractDetails.equipment.contractTypeName}
        contractNumber={contractDetails.equipment.contractNumber}
        contractUsageName={contractDetails.equipment.contractUsageName}
        equipmentNumber={contractDetails.equipment.equipmentNumber}
        country={contractDetails.equipment.country}
      />

      <GoToTopButton />
      <Box
        sx={{
          display: 'flex',
          alignContent: 'center',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <Grid container sx={{ maxWidth: '1200px', alignItems: 'center' }}>
          <CustomMessageToast
            showMessage={showMessage}
            message={t('ReturnSuccessMessage')}
            errorType="success"
          />
          <Stack direction="row" justifyContent="flex-end" width="100%">
            <Stack
              width="100%"
              direction="column"
              alignItems="flex-start"
              spacing={4}
            >
              <ButtonContent
                contractDetail={BaseController.mapResponseContractToInternalContractDetail(
                  contractDetails.equipment,
                )}
                setShowContactForm={setShowContactForm}
                hasActiveCCR={contractDetails.hasActiveCCR}
              />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                width="100%"
                spacing={2}
              >
                <ProductImage
                  serialNumber={
                    BaseController.mapResponseContractToInternalContractDetail(
                      contractDetails.equipment,
                    ).equipmentSerialNr
                  }
                />
                <ContractData
                  contractDetails={BaseController.mapResponseContractToInternalContractDetail(
                    contractDetails.equipment,
                  )}
                />
              </Stack>
              <CustomerContent
                customerMasterData={BaseController.mapResponseCustomerMasterDataToInternalCustomerMasterData(
                  contractDetails.customerMasterData,
                )}
              />

              {contractDetails.invoices && (
                <InvoiceData
                  invoiceData={BaseController.mapResponseToInternalInvoices(
                    contractDetails.invoices,
                  )}
                />
              )}

              <Usage
                agreedOperatingHours={Number(
                  contractDetails.equipment.agreedOperatingHours,
                )}
                prolongedEnd={contractDetails.equipment.prolongedEnd === 'X'}
                contractBegin={new Date(contractDetails.equipment.rcmBeginDate)}
                contractEnd={new Date(contractDetails.equipment.rcmEndDate)}
                operatingHours={contractDetails.operatingHours}
              />

              {contractDetails.documents && (
                <Downloads
                  downloadData={BaseController.mapResponseToInternalContractDocumentData(
                    contractDetails.documents,
                  )}
                  contractNumber={contractDetails.equipment.contractNumber}
                  contractPosition={contractDetails.equipment.contractPosition}
                />
              )}

              <Stack
                justifyContent="center"
                alignContent="center"
                width="100%"
                direction="row"
                style={{ marginTop: '80px', marginBottom: '30px' }}
              >
                <Stack alignItems="center" spacing={4}>
                  <Typography variant="h2">{t('NeedHelp')}</Typography>
                  <Typography variant="h4">{t('Assistance')}</Typography>

                  <CustomButton
                    handleOnClick={() => {
                      setShowContactForm(true);
                    }}
                    text={t('Contact')}
                    sx={{
                      fontFamily: 'JHFontBold',
                      border: '1px solid black',
                    }}
                    color="info"
                  />
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Grid>
      </Box>
    </Box>
  );
};

DetailContent.defaultProps = {
  showMessage: false,
};
